/* -------------------------------------------------------------------------
   Briques UI partagées — charte Aëlvir (noir #0F0D0F, cuivre #C07A4A).

   Le site tient en une page : ce fichier ne contient plus que ce qui sert
   réellement. Les briques de pages intérieures (fil d'Ariane, en-tête de
   page, bande CTA) ont été retirées avec les pages qu'elles servaient —
   elles restent consultables dans frontend/_archive/ si besoin.
   ------------------------------------------------------------------------- */

import { cn } from "../lib/utils";
import { scrollToId } from "../lib/scroll";

const KICKER_TONE = {
  copper: "text-copper",
  muted: "text-dim",
};

/** Sur-titre : filet + libellé (section), filet+point (footer), ou libellé seul. */
export const Kicker = ({
  children,
  tone = "copper",
  variant = "section",
  className = "",
}) => {
  const color = KICKER_TONE[tone] || KICKER_TONE.copper;

  if (variant === "inline") {
    return (
      <p className={cn("text-[10px] uppercase tracking-[0.28em]", color, className)}>
        {children}
      </p>
    );
  }

  if (variant === "footer") {
    return (
      <div className={cn("flex items-center gap-2.5 mb-5", className)}>
        <span className="h-px w-7 sm:w-9 bg-copper/80" aria-hidden />
        <span className="w-1 h-1 rounded-full bg-copper shrink-0" aria-hidden />
        <span className={cn("text-[10px] sm:text-xs uppercase tracking-[0.32em]", color)}>
          {children}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-3 mb-6", className)}>
      <span className="h-px w-12 md:w-16 bg-gradient-brand shrink-0" aria-hidden />
      <span className={cn("text-xs uppercase tracking-[0.35em]", color)}>
        {children}
      </span>
    </div>
  );
};

const CTA_BASE =
  "inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em]";

const CTA_VARIANTS = {
  copper:
    "px-8 py-3.5 rounded-xl text-cream btn-copper",
  outline:
    "px-8 py-3.5 rounded-xl text-cream border border-copper-light/45 bg-transparent hover:border-copper hover:bg-section/70 transition-colors duration-300",
  header:
    "px-6 py-2.5 rounded-md text-cream border border-copper-light/80 bg-copper/15 hover:border-copper-light hover:bg-copper/30 transition-all duration-300",
  ghost:
    "px-7 py-3.5 rounded-xl text-copper border border-copper/70 hover:border-copper hover:text-copper-light transition-colors duration-300 whitespace-nowrap",
};

/** Bouton d'action — copper (plein), outline, header, ghost (bordure cuivre).
 *  Avec `href`, rend un lien (ancres internes ou URL). */
export const CtaButton = ({
  variant = "copper",
  className = "",
  type = "button",
  href,
  onClick,
  children,
  ...props
}) => {
  const classes = cn(CTA_BASE, CTA_VARIANTS[variant], className);

  if (href) {
    const isHash = href.startsWith("#");
    return (
      <a
        href={href}
        className={classes}
        onClick={(e) => {
          onClick?.(e);
          if (isHash && !e.defaultPrevented) {
            e.preventDefault();
            scrollToId(href);
          }
        }}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

/** Titre de section, avec la seconde partie en dégradé cuivre.
 *  `stacked` : accent sur sa propre ligne (ex. FAQ en 2 lignes). */
export const SectionTitle = ({ lead, accent, tail, stacked = false, className = "" }) => (
  <h2
    className={cn("font-display text-3xl md:text-5xl uppercase text-cream", className)}
  >
    {stacked ? <span className="block md:whitespace-nowrap">{lead}</span> : lead}
    {accent && (
      <span
        className={`text-gradient${stacked ? " block md:whitespace-nowrap" : ""}`}
      >
        {stacked ? null : " "}
        {accent}
      </span>
    )}
    {tail ? ` ${tail}` : null}
  </h2>
);

/** Numéro de section — League Spartan contour cuivre, alterne gauche / droite. */
export const Watermark = ({ n, side }) => {
  const align = side ?? (Number(n) % 2 === 1 ? "left" : "right");
  const pos =
    align === "left"
      ? "left-3 md:left-6 xl:left-10 2xl:left-14"
      : "right-3 md:right-6 xl:right-10 2xl:right-14";

  return (
    <span
      aria-hidden
      className={`watermark font-display absolute top-4 md:top-6 ${pos} text-[72px] md:text-[146px] xl:text-[168px] z-0`}
    >
      {String(n).padStart(2, "0").slice(-2)}
    </span>
  );
};

/**
 * Carte « porte » — structure Studio :
 * icône → verbe → titre → accroche → desc → filet → idéal pour.
 *
 * Zone haute en flex-1 + filet en mt-auto : les filets s’alignent
 * entre cartes (à condition que le bas « idéal » ait une hauteur min égale).
 */
export const PortalDoorCard = ({
  icon: Icon,
  verb,
  title,
  hook,
  description,
  idealLabel,
  idealItems = [],
  className = "",
}) => (
  <div
    className={`portal-card group h-full flex flex-col items-center text-center px-7 pt-11 pb-9 md:px-8 md:pt-12 md:pb-10 ${className}`}
  >
    <div className="flex-1 flex flex-col items-center w-full min-h-0">
      <div className="portal-icon mb-5">
        <img
          src="/images/portal-halo.webp"
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="portal-icon-halo-img"
          draggable={false}
        />
        <span className="portal-icon-ring-inner">
          <Icon
            size={54}
            strokeWidth={1.15}
            className="portal-icon-glyph"
          />
        </span>
      </div>

      {verb && (
        <span className="text-sm uppercase tracking-[0.16em] text-copper">
          {verb}
        </span>
      )}

      <h3 className="font-display text-xl font-normal text-cream mt-7 mb-3 leading-snug">
        {title}
      </h3>

      {hook && (
        <p className="text-sm text-warm font-light leading-snug max-w-[16rem] mb-4">
          {hook}
        </p>
      )}

      <p className="text-sm text-warm/90 font-light leading-relaxed max-w-[16rem]">
        {description}
      </p>

      <div className="mt-auto w-full flex flex-col items-center justify-center pt-8 pb-5">
        <span className="portal-card-rule" aria-hidden />
      </div>
    </div>

    {idealLabel && idealItems.length > 0 && (
      <div className="w-full max-w-[16.5rem] min-h-[10.5rem]">
        <p className="text-[11px] uppercase tracking-[0.28em] text-copper mb-3.5 text-center">
          {idealLabel}
        </p>
        <ul className="space-y-2.5 text-left">
          {idealItems.map((item) => {
            const ItemIcon = item.icon;
            return (
              <li
                key={item.label}
                className="flex items-center justify-start gap-2.5 text-sm text-warm font-light leading-snug"
              >
                {ItemIcon && (
                  <ItemIcon
                    size={15}
                    strokeWidth={1.5}
                    className="shrink-0 text-copper"
                    aria-hidden
                  />
                )}
                <span>{item.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    )}
  </div>
);
